import { useEffect, useRef, useState } from "react";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Loader2, MapPin, CheckCircle2 } from "lucide-react";
import { loadGoogleMaps } from "../lib/googleMaps";
import { api } from "../lib/config";
import { useAuth } from "../contexts/AuthContext";

/**
 * AddressAutocomplete — Google Places Autocomplete restricted to FL, US.
 * Calls `onPlace({address, formatted_address, place_id, lat, lng, acres, square_feet, source})`
 * whenever a real place is selected. `acres / square_feet` are best-effort from OSM.
 */
export default function AddressAutocomplete({
  id = "address",
  value,
  onChange,
  onPlace,
  label = "Service address",
  required = true,
  testId = "book-address",
  placeholder,
  showInlineSummary = true,
}) {
  const inputRef = useRef(null);
  const acRef = useRef(null);
  const { user } = useAuth();
  const isAdmin = !!user?.is_admin;
  const [ready, setReady] = useState(false);
  const [lookingUp, setLookingUp] = useState(false);
  const [propertyInfo, setPropertyInfo] = useState(null);
  const [error, setError] = useState(null);

  // iter153-hotfix — Keep the latest callbacks in refs so the setup
  // effect below can run ONCE (empty dep array). Otherwise every parent
  // re-render creates a fresh onPlace/onChange reference, which would
  // (a) tear down and re-attach the Google Places Autocomplete on every
  // keystroke and (b) stack multiple hidden listeners on the same DOM
  // input — on mobile Safari/Chrome this manifests as the input
  // "freezing" after the first character.
  const onChangeRef = useRef(onChange);
  const onPlaceRef = useRef(onPlace);
  useEffect(() => { onChangeRef.current = onChange; });
  useEffect(() => { onPlaceRef.current = onPlace; });

  useEffect(() => {
    let cancelled = false;
    loadGoogleMaps()
      .then((google) => {
        if (cancelled || !inputRef.current) return;
        const ac = new google.maps.places.Autocomplete(inputRef.current, {
          types: ["address"],
          componentRestrictions: { country: ["us"] },
          fields: ["place_id", "formatted_address", "geometry", "address_components", "name"],
        });
        acRef.current = ac;
        ac.addListener("place_changed", async () => {
          const place = ac.getPlace();
          if (!place || !place.geometry || !place.geometry.location) return;
          const lat = place.geometry.location.lat();
          const lng = place.geometry.location.lng();
          const formatted = place.formatted_address || place.name || "";
          const place_id = place.place_id;

          // Set the form value immediately
          if (onChangeRef.current) onChangeRef.current(formatted);

          // Best-effort acreage lookup
          setLookingUp(true);
          setError(null);
          try {
            const { data } = await api.post("/property/lookup", {
              lat, lng, address: formatted,
            });
            // Forward the FULL property payload so consumers (e.g. EstimateCalculator)
            // can render the building footprint / yard breakdown.
            const info = {
              ...data,
              address: formatted,
              formatted_address: formatted,
              place_id,
              lat,
              lng,
              source: data.source || "google",
            };
            setPropertyInfo(info);
            if (onPlaceRef.current) onPlaceRef.current(info);
          } catch (e) {
            const info = {
              address: formatted,
              formatted_address: formatted,
              place_id,
              lat,
              lng,
              acres: null,
              square_feet: null,
              source: "google",
            };
            setPropertyInfo(info);
            if (onPlaceRef.current) onPlaceRef.current(info);
          } finally {
            setLookingUp(false);
          }
        });
        setReady(true);
      })
      .catch((e) => {
        setError(e.message || "Could not load address autocomplete");
        setReady(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div>
      {label && (
        <Label htmlFor={id} className="text-xs uppercase tracking-[0.16em] text-gray-500 font-bold">
          {label}
        </Label>
      )}
      <div className="mt-1.5 relative">
        <MapPin className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 z-10 pointer-events-none" />
        <Input
          ref={inputRef}
          id={id}
          type="text"
          {...(value !== undefined ? { value, onChange: (e) => onChangeRef.current && onChangeRef.current(e.target.value) } : {})}
          placeholder={placeholder || (ready ? "Start typing your address…" : "Loading address search…")}
          required={required}
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="off"
          spellCheck={false}
          inputMode="text"
          onFocus={(e) => e.currentTarget.select()}
          className="h-12 pl-10 pr-10 rounded-xl bg-white text-gray-900 font-semibold placeholder:text-gray-400 caret-emerald-600 cursor-text focus-visible:ring-2 focus-visible:ring-emerald-600 border-gray-300"
          data-testid={testId}
        />
        {lookingUp && (
          <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-[#10B981]" />
        )}
        {!lookingUp && propertyInfo && (
          <CheckCircle2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#10B981]" />
        )}
      </div>

      {/* Property info chip (suppressed when consumer renders its own breakdown) */}
      {showInlineSummary && propertyInfo && (
        <div className="mt-3 rounded-2xl bg-emerald-50 border border-emerald-200 p-3 sm:p-4" data-testid="property-info">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] text-[#047857] font-extrabold">
            <CheckCircle2 className="w-3.5 h-3.5" /> Address verified
          </div>
          <div className="mt-1 text-sm font-bold text-gray-900 leading-tight">{propertyInfo.formatted_address}</div>
          {isAdmin && propertyInfo.acres ? (
            <div className="mt-2 flex flex-wrap gap-1.5">
              <span className="inline-flex items-center text-[11px] font-extrabold uppercase tracking-[0.14em] px-2.5 py-1 rounded-full bg-white border border-emerald-200 text-[#047857]" data-testid="property-acres">
                ~{propertyInfo.acres} acres
              </span>
              <span className="inline-flex items-center text-[11px] font-extrabold uppercase tracking-[0.14em] px-2.5 py-1 rounded-full bg-white border border-emerald-200 text-[#047857]" data-testid="property-sqft">
                ~{propertyInfo.square_feet?.toLocaleString()} sqft
              </span>
              <span className="inline-flex items-center text-[11px] font-bold uppercase tracking-[0.14em] px-2.5 py-1 rounded-full bg-emerald-100 text-emerald-700">
                lot est.
              </span>
            </div>
          ) : (
            <div className="mt-2 text-xs text-gray-600">
              Address verified — we&apos;ll calculate your custom price.
            </div>
          )}
        </div>
      )}

      {error && (
        <div className="mt-2 text-xs text-amber-700">
          Autocomplete unavailable — please type your full address manually.
        </div>
      )}
    </div>
  );
}
